import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatError } from "../utils/api";
import Navbar from "../components/Navbar";
import DocHelpButton from "../components/DocHelpButton";
import SetupTab from "../components/wizard/SetupTab";
import ProcessStagesTab from "../components/wizard/ProcessStagesTab";
import CalibrateTab from "../components/wizard/CalibrateTab";
import CurrentStateTab from "../components/wizard/CurrentStateTab";
import FutureStateTab from "../components/wizard/FutureStateTab";
import DemandPlannerTab from "../components/wizard/DemandPlannerTab";
import TimwoodTab from "../components/wizard/TimwoodTab";
import DiagnosticTab from "../components/wizard/DiagnosticTab";
import { calcEffectiveHours } from "../utils/calculations";
import { exportWorkbookExcel } from "../utils/exportExcel";
import { ChevronLeft, ChevronRight, Plus, Check, Download, FileText, FileSpreadsheet, Share2 as PortalIcon, Copy as CopyIcon, X as CloseIcon } from "lucide-react";

const TABS = [
  { n: 1, label: "Setup & Context" },
  { n: 2, label: "Process Stages" },
  { n: 3, label: "Calibrate" },
  { n: 4, label: "Current State" },
  { n: 5, label: "Future State" },
  { n: 6, label: "Demand Planner" },
  { n: 7, label: "TIMWOOD" },
  { n: 8, label: "Diagnostic" },
];

export default function WorkspacePage() {
  const { companyId, projectId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [branding, setBranding] = useState(null);
  const [currentTab, setCurrentTab] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newProjName, setNewProjName] = useState("");
  const [newProjToolType, setNewProjToolType] = useState("staffing_assessment");
  const [showNewProj, setShowNewProj] = useState(false);
  const [portalInfo, setPortalInfo] = useState(null);
  const [portalCopied, setPortalCopied] = useState(false);
  const saveTimeout = useRef({});

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, projectId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [compRes, projsRes, brandRes] = await Promise.all([
        api.get(`/companies/${companyId}`),
        api.get(`/companies/${companyId}/projects`),
        api.get(`/my/branding`).catch(() => ({ data: { branding: null } })),
      ]);
      setCompany(compRes.data);
      setProjects(projsRes.data);
      setBranding(brandRes.data?.branding || null);
      if (projectId) {
        const pRes = await api.get(`/projects/${projectId}`);
        if (pRes.data.tool_type && pRes.data.tool_type !== "staffing_assessment") {
          navigate(`/tool/${companyId}/project/${projectId}`, { replace: true });
          return;
        }
        setProject(pRes.data);
        setCurrentTab(pRes.data.wizard_progress || 1);
      } else {
        // No projectId in route — let the parent /workspace/:companyId route render the overview.
        navigate(`/workspace/${companyId}`, { replace: true });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (tool_type = "staffing_assessment") => {
    if (!newProjName.trim()) return;
    try {
      const res = await api.post(`/companies/${companyId}/projects`, { name: newProjName, tool_type });
      setProjects((p) => [res.data, ...p]);
      setNewProjName("");
      setShowNewProj(false);
      const isTool = tool_type !== "staffing_assessment";
      navigate(`${isTool ? "/tool" : "/workspace"}/${companyId}/project/${res.data.id}`);
    } catch (e) {
      alert(formatError(e));
    }
  };

  const debounceSave = useCallback((key, fn, delay = 1500) => {
    if (saveTimeout.current[key]) clearTimeout(saveTimeout.current[key]);
    setSaving(true);
    saveTimeout.current[key] = setTimeout(async () => {
      try { await fn(); } catch (e) { console.error("Save error:", e); } finally { setSaving(false); }
    }, delay);
  }, []);

  const updateSetup = useCallback((section, data) => {
    setProject((prev) => {
      const updated = { ...prev, setup: { ...prev.setup, [section]: { ...prev.setup[section], ...data } } };
      debounceSave("setup", () => api.put(`/projects/${prev.id}/setup`, { [section]: updated.setup[section] }));
      return updated;
    });
  }, [debounceSave]);

  const updateStages = useCallback((stages) => {
    setProject((prev) => {
      const updated = { ...prev, stages };
      debounceSave("stages", () => api.put(`/projects/${prev.id}/stages`, { stages }));
      return updated;
    });
  }, [debounceSave]);

  const updateCalibration = useCallback((cal) => {
    setProject((prev) => {
      const updated = { ...prev, calibration: cal };
      debounceSave("calibration", () => api.put(`/projects/${prev.id}/calibration`, cal));
      return updated;
    });
  }, [debounceSave]);

  const updateDemand = useCallback((demand) => {
    setProject((prev) => {
      const updated = { ...prev, demand };
      debounceSave("demand", () => api.put(`/projects/${prev.id}/demand`, demand));
      return updated;
    });
  }, [debounceSave]);

  const updateDiagnostic = useCallback((diag) => {
    setProject((prev) => ({ ...prev, diagnostic: diag }));
  }, []);

  const goToTab = async (tab) => {
    if (!project) return;
    const maxAllowed = Math.max(project.wizard_progress || 1, currentTab);
    if (tab > maxAllowed + 1) return;
    if (tab > (project.wizard_progress || 1)) {
      try {
        await api.put(`/projects/${project.id}/progress`, { progress: tab });
        setProject((p) => ({ ...p, wizard_progress: tab }));
      } catch (e) {}
    }
    setCurrentTab(tab);
    window.scrollTo(0, 0);
  };

  const handleNext = () => goToTab(Math.min(currentTab + 1, 8));
  const handleBack = () => setCurrentTab((t) => Math.max(t - 1, 1));

  const handleExportCSV = async () => {
    if (!project) return;
    const token = localStorage.getItem("sage_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${project.id}/export/csv`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `sage-${project.name}.csv`; a.click();
  };

  const handleExportExcel = () => {
    if (!project) return;
    exportWorkbookExcel({ project, company, effectiveHours: effHours });
  };

  const handleCreatePortal = async () => {
    try {
      const res = await api.post(`/companies/${companyId}/portal`, { expires_days: 90 });
      const url = `${window.location.origin}/portal/${res.data.token}`;
      setPortalInfo({ url, expires_at: res.data.expires_at });
    } catch (err) {
      alert(formatError(err));
    }
  };

  const handleRevokePortal = async () => {
    try {
      await api.delete(`/companies/${companyId}/portal`);
      setPortalInfo(null);
    } catch (err) { alert(formatError(err)); }
  };

  const handleCopyPortal = () => {
    if (!portalInfo) return;
    navigator.clipboard.writeText(portalInfo.url);
    setPortalCopied(true);
    setTimeout(() => setPortalCopied(false), 2000);
  };

  const effHours = project ? calcEffectiveHours(project.setup?.effective_hours) : null;

  const tabProps = { project, company, branding, effectiveHours: effHours, updateSetup, updateStages, updateCalibration, updateDemand, updateDiagnostic };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-sage-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Navbar saving={saving} />
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Company header + project selector */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/workspace/${companyId}`)} className="text-sage-400 hover:text-sage-700 transition-colors" data-testid="back-to-overview">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-heading text-lg font-semibold text-sage-900">{company?.name}</h2>
                <span className="text-xs bg-sage-100 text-sage-600 px-2 py-0.5 rounded-full">{company?.domain}</span>
              </div>
              <p className="text-xs text-sage-400">{company?.country} · {company?.currency}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {project?.tool_type && <DocHelpButton toolId={project.tool_type} label="Read docs" />}
            {projects.length > 0 && (
              <select
                data-testid="project-selector"
                value={projectId || ""}
                onChange={(e) => {
                  const selected = projects.find(p => p.id === e.target.value);
                  const base = selected?.tool_type && selected.tool_type !== "staffing_assessment" ? "/tool" : "/workspace";
                  navigate(`${base}/${companyId}/project/${e.target.value}`);
                }}
                className="border border-sage-200 rounded-md px-3 py-1.5 text-sm bg-white text-sage-800 focus:outline-none focus:border-sage-400"
              >
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}{p.tool_type && p.tool_type !== "staffing_assessment" ? ` · ${p.tool_type}` : ""}</option>)}
              </select>
            )}
            {showNewProj ? (
              <div className="flex items-center gap-1">
                <input value={newProjName} onChange={(e) => setNewProjName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createProject(newProjToolType)} placeholder="Project name" className="border border-sage-200 rounded-md px-2 py-1.5 text-sm w-36 focus:outline-none" autoFocus />
                <select data-testid="new-project-tool-type" value={newProjToolType} onChange={(e) => setNewProjToolType(e.target.value)} className="border border-sage-200 rounded-md px-2 py-1.5 text-sm bg-white">
                  <option value="staffing_assessment">Staffing</option>
                  <option value="vsm_builder">VSM</option>
                  <option value="sipoc_mapper">SIPOC</option>
                  <option value="voc_kano">VoC + Kano</option>
                  <option value="time_study">Time Study</option>
                  <option value="waste_walk">Waste Walk</option>
                  <option value="rca_5whys_fishbone">5 Whys + Fishbone</option>
                  <option value="pareto_analyzer">Pareto</option>
                  <option value="spaghetti_diagram">Spaghetti</option>
                  <option value="pfmea">PFMEA</option>
                  <option value="a3_problem_solving">A3</option>
                  <option value="dmaic_charter">DMAIC</option>
                  <option value="yamazumi">Yamazumi</option>
                  <option value="impact_effort">Impact × Effort</option>
                  <option value="five_s_audit">5S Audit</option>
                  <option value="kaizen_charter">Kaizen</option>
                  <option value="maturity_assessment">Maturity</option>
                </select>
                <button onClick={() => createProject(newProjToolType)} className="bg-sage-600 text-white rounded-md px-2 py-1.5 text-xs">Add</button>
                <button onClick={() => setShowNewProj(false)} className="text-sage-400 hover:text-sage-600 px-1 py-1.5 text-xs">✕</button>
              </div>
            ) : (
              <button onClick={() => setShowNewProj(true)} data-testid="new-project-button" className="flex items-center gap-1 border border-sage-200 text-sage-600 rounded-md px-3 py-1.5 text-xs hover:bg-sage-50 transition-colors">
                <Plus className="w-3 h-3" /> New Project
              </button>
            )}
            {project && (
              <button onClick={handleExportCSV} data-testid="export-csv-button" className="flex items-center gap-1 border border-sage-200 text-sage-600 rounded-md px-3 py-1.5 text-xs hover:bg-sage-50 transition-colors">
                <Download className="w-3 h-3" /> CSV
              </button>
            )}
            {project && (
              <button onClick={handleExportExcel} data-testid="export-excel-button" className="flex items-center gap-1 border border-sage-200 text-sage-600 rounded-md px-3 py-1.5 text-xs hover:bg-sage-50 transition-colors">
                <FileSpreadsheet className="w-3 h-3" /> Excel
              </button>
            )}
            <button onClick={handleCreatePortal} data-testid="client-portal-button" className="flex items-center gap-1 border border-sage-200 text-sage-600 rounded-md px-3 py-1.5 text-xs hover:bg-sage-50 transition-colors">
              <PortalIcon className="w-3 h-3" /> Client Portal
            </button>
          </div>
        </div>

        {portalInfo && (
          <div className="bg-sage-50 border border-sage-200 rounded-md p-3 mb-4 flex items-start justify-between gap-3" data-testid="portal-link-box">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-sage-600 mb-1">Client portal link · all projects · expires {new Date(portalInfo.expires_at).toLocaleDateString()}</p>
              <code className="text-xs bg-white border border-sage-200 rounded px-2 py-1 block truncate select-all text-sage-800" data-testid="portal-link-url">{portalInfo.url}</code>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button data-testid="copy-portal-link" onClick={handleCopyPortal} className="border border-sage-300 text-sage-700 rounded-md px-2 py-1 text-xs hover:bg-white flex items-center gap-1">
                <CopyIcon className="w-3 h-3" /> {portalCopied ? "Copied" : "Copy"}
              </button>
              <button data-testid="revoke-portal-link" onClick={handleRevokePortal} className="border border-red-200 text-red-600 rounded-md px-2 py-1 text-xs hover:bg-red-50 flex items-center gap-1">
                <CloseIcon className="w-3 h-3" /> Revoke
              </button>
            </div>
          </div>
        )}

        {!project ? (
          <div className="text-center py-16 bg-white border border-dashed border-sage-200 rounded-md">
            <FileText className="w-10 h-10 text-sage-300 mx-auto mb-3" />
            <h3 className="font-heading text-lg font-medium text-sage-700 mb-2">No simulation project yet</h3>
            <p className="text-sage-400 text-sm mb-4">Create your first project to start the analysis wizard</p>
            <button onClick={() => setShowNewProj(true)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 transition-colors">
              Create First Project
            </button>
          </div>
        ) : (
          <>
            {/* Wizard Progress */}
            <div className="bg-white border border-[#E2E8E3] rounded-md p-3 mb-4 overflow-x-auto">
              <div className="flex items-center min-w-max">
                {TABS.map((tab, i) => {
                  const completed = (project.wizard_progress || 1) > tab.n;
                  const active = currentTab === tab.n;
                  const maxReach = Math.max(project.wizard_progress || 1, currentTab);
                  const accessible = tab.n <= maxReach + 1;
                  return (
                    <div key={tab.n} className="flex items-center">
                      <button
                        data-testid={`wizard-tab-${tab.n}`}
                        onClick={() => accessible && goToTab(tab.n)}
                        disabled={!accessible}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap
                          ${active ? "bg-sage-600 text-white" : completed ? "text-sage-600 hover:bg-sage-50" : accessible ? "text-sage-400 hover:bg-sage-50" : "text-sage-200 cursor-not-allowed"}`}
                      >
                        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs flex-shrink-0 ${active ? "bg-white/20" : completed ? "bg-sage-500 text-white" : "bg-sage-100"}`}>
                          {completed ? <Check className="w-2.5 h-2.5" /> : <span className="text-[10px]">{tab.n}</span>}
                        </span>
                        {tab.label}
                      </button>
                      {i < TABS.length - 1 && (
                        <div className={`w-6 h-px mx-1 flex-shrink-0 ${completed || active ? "bg-sage-400" : "bg-sage-100"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tab content */}
            <div className="animate-fade-in">
              {currentTab === 1 && <SetupTab {...tabProps} />}
              {currentTab === 2 && <ProcessStagesTab {...tabProps} />}
              {currentTab === 3 && <CalibrateTab {...tabProps} />}
              {currentTab === 4 && <CurrentStateTab {...tabProps} />}
              {currentTab === 5 && <FutureStateTab {...tabProps} />}
              {currentTab === 6 && <DemandPlannerTab {...tabProps} />}
              {currentTab === 7 && <TimwoodTab {...tabProps} />}
              {currentTab === 8 && <DiagnosticTab {...tabProps} />}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-sage-100">
              <button
                data-testid="wizard-back-button"
                onClick={handleBack}
                disabled={currentTab === 1}
                className="flex items-center gap-2 border border-sage-200 text-sage-700 rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <span className="text-xs text-sage-400 font-body">Step {currentTab} of 8</span>
              {currentTab < 8 ? (
                <button
                  data-testid="wizard-next-button"
                  onClick={handleNext}
                  className="flex items-center gap-2 bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <div className="text-xs text-sage-400">Use the "Run Diagnostic" button above</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
